import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Chart as ChartJS } from "chart.js";
import { Line } from "react-chartjs-2";
import { TbCreditCard, TbTrendingUp } from "react-icons/tb";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { useColorMode } from "../../../../../Hooks/useColorMode";
import { fetchBaasCardPrograms, fetchBaasPeerCardPrograms } from "../../../../../Services/baasCardProgramsApi";
import { formatMonetaryValue } from "../../../../../Utils/formatters";
import { shouldShowBaasCustodyAggregate } from "../../baasInstanceListFilter";
import {
  aggregateBaasProgramsCardsMetrics,
  EMPTY_BAAS_HOME_CARDS_METRICS,
  getMockBaasHomeCardsVolumeChart,
} from "../baasHomeMetrics";
import {
  buildBaasHomeLineChartOptions,
  registerBaasHomeLineChart,
} from "./BaasHomeChartOptions";
import { BaasHomeSnapshotSection } from "./BaasHomeSnapshotSection";

registerBaasHomeLineChart(ChartJS);

const formatCount = (value) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(value);

const formatPercent = (value, fractionDigits = 1) =>
  `${new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)}%`;

const DisabledIssuerPanel = ({ message }) => (
  <div className="app-card flex h-full min-h-[12rem] flex-1 flex-col items-center justify-center gap-3 border border-dashed border-gray-200 bg-gray-50/80 p-6 text-center opacity-80 dark:border-brand-dark-border dark:bg-brand-dark-surface/60">
    <div className="summary-card-icon opacity-60">
      <TbCreditCard className="h-5 w-5 text-gray-400" aria-hidden />
    </div>
    <p className="max-w-xs text-sm text-gray-500 dark:text-gray-400">{message}</p>
  </div>
);

export const BaasHomeCardsPanel = ({ displayCurrency = "BRL", embedded = false }) => {
  const { selectedInstance, listInstanceFilter } = useBaasInstance();
  const { can, getBlockReason, loading: capabilitiesLoading } = useBaasCapabilities();
  const { isDark } = useColorMode();
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);

  const issuerAllowed = !capabilitiesLoading && can("issuer.panel.view");
  const issuerBlockReason = getBlockReason("issuer.panel.view");
  const disabledMessage = capabilitiesLoading
    ? null
    : issuerBlockReason === "product_disabled"
      ? intl.get("baas.home.capabilities.issuerDisabled")
      : intl.get("baas.home.capabilities.permissionDenied");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedInstance || !issuerAllowed) {
        setPrograms([]);
        setProgramsLoading(false);
        return;
      }

      setProgramsLoading(true);
      try {
        const [own, peers] = await Promise.all([
          fetchBaasCardPrograms(selectedInstance),
          shouldShowBaasCustodyAggregate(selectedInstance, listInstanceFilter)
            ? fetchBaasPeerCardPrograms(selectedInstance)
            : Promise.resolve([]),
        ]);
        if (!cancelled) {
          const byId = new Map();
          for (const row of own) {
            if (!row?.id) continue;
            byId.set(row.id, row);
          }
          for (const row of peers) {
            if (!row?.id) continue;
            byId.set(row.id, row);
          }
          setPrograms([...byId.values()]);
        }
      } catch {
        if (!cancelled) setPrograms([]);
      } finally {
        if (!cancelled) setProgramsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [issuerAllowed, selectedInstance, listInstanceFilter]);

  const cardsMetrics = useMemo(
    () =>
      programsLoading
        ? EMPTY_BAAS_HOME_CARDS_METRICS
        : aggregateBaasProgramsCardsMetrics(programs),
    [programs, programsLoading],
  );
  const volumeChart = useMemo(
    () => getMockBaasHomeCardsVolumeChart(displayCurrency),
    [displayCurrency],
  );
  const currency = volumeChart.currency || displayCurrency;
  const showChangePercent = Number(volumeChart.changePercent) !== 0;
  const isPositive = volumeChart.changePercent > 0;

  const formatTooltipValue = useCallback(
    (value) => formatMonetaryValue(value, currency, { maximumFractionDigits: 0 }),
    [currency],
  );

  const chartOptions = useMemo(
    () => buildBaasHomeLineChartOptions(isDark, formatTooltipValue),
    [isDark, formatTooltipValue],
  );

  const chartData = useMemo(
    () => ({
      labels: volumeChart.labels,
      datasets: [
        {
          label: intl.get("baas.home.dashboard.cardsChart.dataset"),
          data: volumeChart.values,
          borderColor: "#e7a73f",
          backgroundColor: "rgba(231, 167, 63, 0.12)",
          fill: true,
          pointBackgroundColor: "#e7a73f",
        },
      ],
    }),
    [volumeChart.labels, volumeChart.values],
  );

  return (
    <BaasHomeSnapshotSection
      embedded={embedded}
      title={intl.get("baas.home.cardsSection")}
      viewTo={issuerAllowed ? "/baas/cartoes" : null}
      viewLabel={intl.get("baas.home.dashboard.viewEmissor")}
    >
      {capabilitiesLoading ? (
        <DisabledIssuerPanel message="Carregando…" />
      ) : !issuerAllowed ? (
        <DisabledIssuerPanel message={disabledMessage} />
      ) : (
        <div className="app-card flex h-full min-h-0 flex-1 flex-col overflow-hidden">
          <div className="border-b border-gray-100 px-4 py-4 dark:border-brand-dark-border sm:px-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex flex-wrap gap-6 sm:gap-8">
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {intl.get("baas.home.activeCards.title")}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-brand-dark dark:text-gray-100">
                    {formatCount(cardsMetrics.activeCards)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {intl.get("baas.home.approvalRate.title")}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-brand-dark dark:text-gray-100">
                    {formatPercent(cardsMetrics.approvalRate)}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {intl.get("baas.home.approvalRate.subtext")}
                  </p>
                </div>
              </div>
              {showChangePercent ? (
                <div
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    isPositive
                      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                      : "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300"
                  }`}
                >
                  <TbTrendingUp className={`h-3.5 w-3.5 ${isPositive ? "" : "rotate-180"}`} />
                  {isPositive ? "+" : ""}
                  {formatPercent(volumeChart.changePercent, 1)}{" "}
                  {intl.get("baas.home.dashboard.cardsChart.changeSuffix")}
                </div>
              ) : null}
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.home.dashboard.cardsChart.periodTotal", {
                amount: formatMonetaryValue(volumeChart.periodTotal, currency, {
                  maximumFractionDigits: 0,
                }),
              })}
            </p>
          </div>

          <div className="flex min-h-0 flex-1 flex-col px-2 pb-3 pt-2 sm:px-4">
            <p className="mb-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.home.dashboard.cardsChart.title")}
            </p>
            <div className="min-h-40 w-full flex-1 sm:min-h-44">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      )}
    </BaasHomeSnapshotSection>
  );
};
